import { Validation } from './Validation';
import { EventTemplateStatus } from './EventTemplateStatus';
import CategoryTypeJson from './CategoryTypeJson';
import EventDateJson, { validateEventDateJson } from './EventDateJson'
import PriceJson, { defaultPriceJson, validateEventPriceJson, validateEventPriceJsonAddNew } from './PriceJson';
import CustomInputJson, { validateCustomInputJson } from "./CustomInputJson";
import EventBookingJson from './EventBookingJson';
import EventImageJson, { validateEventImageJson, validateEventImageJsonAdd } from './EventImageJson';

/**
 * Vorlage einer Veranstaltung
 */
export default interface EventTemplateJson {
   ID: number | null,

   /**
    * Titel der Vorlage
    */
   title: string,

   /**
    * Status der Vorlage.
    */
   state: EventTemplateStatus;

   /**
    * Besondere Hinweise die für diesen Kurs gelten. Kann ein leerer Ttext sein wenn nichts besonderes gilt.
    */
   remarks: string;

   /**
    * Titel der Veranstaltung
    */
   eventTitle: string,

   /**
    * Beschreibung der Veranstaltung.
    */
   eventDescription: string;

   /**
    * Bilder zur Veranstaltung. Max 5 Bilder.
    */
   eventImages: EventImageJson[];

   /**
    * Diese Veranstaltung ist intern und nur fuer angemeldete User sichtbar.
    */
   eventInternal: boolean;

   /**
    * Gibt Kategorien an, auf die diese Veranstaltung beschränkt ist. Kann null sein, wenn keine Beschränkung vorliegt.
    */
   eventRestrictToTypes: CategoryTypeJson[] | null;

   /**
    * Liste mit Preiskategorien. 
    */
   eventPrices: PriceJson[];

   /**
    * Liste mit zusätzlichen Preisen.
    */
   eventAddonPrices: PriceJson[];

   /**
    * Minimale Anzahl an Teilnehmern die benötigt werden.
    */
   eventRegistrationMinimumCount: number;

   /**
    * Maximale Anzahl an Teilnehmern die akzeptiert werden.
    */
   eventRegistrationMaximumCount: number;

   /**
    * Maximale Anzahl an Teilnehmern die in der Warteliste akzeptiert werden. Kann 0 sein.
    */
   eventWaitingListMaximumCount: number;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Mindestalter die Teilnehmenden für die Veranstaltung haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   eventRestrictMinimumAge: number | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Maximalalter die Teilnehmenden für die Veranstaltung haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   eventRestrictMaximumAge: number | null;

   /**
    * Zum Erstellen individueller Form Inputs
    */
   eventCustomInputs: CustomInputJson[];
}

export function defaultEventTemplateJson(): EventTemplateJson {
   return {
      ID: null,
      title: "",
      state: EventTemplateStatus.draft,
      remarks: "",
      eventTitle: "",
      eventDescription: "",
      eventImages: [],
      eventInternal: false,
      eventRestrictToTypes: null,
      eventPrices: [{ ...defaultPriceJson() }],
      eventAddonPrices: [],
      eventRegistrationMinimumCount: 1,
      eventRegistrationMaximumCount: 10,
      eventWaitingListMaximumCount: 0,
      eventRestrictMinimumAge: null,
      eventRestrictMaximumAge: null,
      eventCustomInputs: [],
   };
}

export function getDuplicateEventTemplateJson(json: EventTemplateJson): EventTemplateJson {
   function setIdsNull<T extends { ID: number | null}>(items: T[]): T[] {
      for(let item of items) {
         item.ID = null;
      }
      return items;
   }
   
   return {
      ...json,
      ID: null,
      title: json.title + ' Kopie',
      eventImages: setIdsNull(json.eventImages),
      eventPrices: setIdsNull(json.eventPrices),
      eventAddonPrices: setIdsNull(json.eventAddonPrices),
   };
}

export function validateEventTemplateJsonManagementAdd(item: EventTemplateJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateNonEmptyString("title", 100)
      .validateString("remarks", 500)
      .validateNonEmptyString("eventTitle", 100)
      .validateString("eventDescription", 1500)
      .validateAllUsingFunction("eventImages", validateEventImageJsonAdd, 0, false, 5)
      .validateAllUsingFunction("eventPrices", validateEventPriceJsonAddNew, 1)
      .validateAllUsingFunction("eventAddonPrices", validateEventPriceJsonAddNew)
      .validateInteger("eventRegistrationMinimumCount", 0)
      .validateInteger("eventRegistrationMaximumCount", 0)
      .validateInteger("eventWaitingListMaximumCount", 0)
      .validateInteger("eventRestrictMinimumAge", 0, 120, true)
      .validateInteger("eventRestrictMaximumAge", 0, 120, true)
      .validateAllUsingFunction("eventCustomInputs", validateCustomInputJson)
      .finish();
}

export function validateEventTemplateJsonManagement(item: EventTemplateJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateNonEmptyString("title", 100)
      .validateString("remarks", 500)
      .validateNonEmptyString("eventTitle", 100)
      .validateString("eventDescription", 1500)
      .validateAllUsingFunction("eventImages", validateEventImageJson, 0, false, 5)
      .validateAllUsingFunction("eventPrices", validateEventPriceJson, 1)
      .validateAllUsingFunction("eventAddonPrices", validateEventPriceJson)
      .validateInteger("eventRegistrationMinimumCount", 0)
      .validateInteger("eventRegistrationMaximumCount", 0)
      .validateInteger("eventWaitingListMaximumCount", 0)
      .validateInteger("eventRestrictMinimumAge", 0, 120, true)
      .validateInteger("eventRestrictMaximumAge", 0, 120, true)
      .validateAllUsingFunction("eventCustomInputs", validateCustomInputJson)
      .finish();
}